import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { centsText, parseBp, parseCents } from '../../money';
import { formatCents, SALES_INVOICE_KIND_LABELS, SALES_INVOICE_STATUS_LABELS, type OrderStatus, type Paginated, type SalesInvoice, type SalesInvoiceSummary } from '@kontor/shared';
import { api, ApiError, queryString } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { useSession } from '../../session';
import { invoiceTone } from './InvoicesPage';
import './invoices.css';

/**
 * Rechnungen zu einem Auftrag, als Karte in der Auftragsansicht (KON-151). Zeigt die Rechnungen des Auftrags und bietet bei einem erledigten Auftrag
 * „Rechnung erstellen“ an. Die Rechnung entsteht als Entwurf mit allen Positionen des Auftrags. Ohne das Modul oder Leserecht erscheint nichts.
 */
export function OrderInvoices({ orderId, status }: { orderId: string; status: OrderStatus }) {
  const { can, hasModule } = useSession();
  const navigate = useNavigate();
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const enabled = hasModule('invoices_out') && can('invoices_out.read');
  const list = useQuery({
    queryKey: ['invoices-out', { orderId }],
    queryFn: async () => (await api<Paginated<SalesInvoiceSummary>>('GET', `/invoices-out${queryString({ orderId, perPage: 20 })}`)).data,
    enabled,
  });
  const create = useMutation({
    mutationFn: async () => (await api<{ data: SalesInvoice }>('POST', '/invoices-out', { orderId })).data,
    onSuccess: (i) => {
      client.invalidateQueries({ queryKey: ['invoices-out'] });
      navigate(`/rechnungen/${i.id}`);
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Die Rechnung konnte nicht angelegt werden.'),
  });
  const [asking, setAsking] = useState(false);
  if (!enabled) return null;

  const invoices = list.data ?? [];
  const finished = invoices.some((i) => i.kind === 'invoice' || i.kind === 'final');
  const hasPrepayment = invoices.some((i) => i.kind === 'partial' && i.status === 'issued');
  const hasDraftPrepayment = invoices.some((i) => i.kind === 'partial' && i.status === 'draft');
  const mayWrite = can('invoices_out.write');
  const canCreate = mayWrite && status === 'done' && !finished && !hasDraftPrepayment;
  const canPrepay = mayWrite && !finished && (status === 'open' || status === 'planned' || status === 'in_progress' || status === 'done');
  if (invoices.length === 0 && !canCreate && !canPrepay && !list.isLoading) return null;

  return (
    <CollapsibleCard
      title="Rechnungen"
      storageKey="order-invoices"
      defaultOpen
      actions={
        <>
          {canPrepay && !asking && <button type="button" className="btn btn--secondary btn--sm" onClick={() => setAsking(true)}>Anzahlung anfordern</button>}
          {canCreate && <button type="button" className="btn btn--primary btn--sm" disabled={create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Wird angelegt …' : hasPrepayment ? 'Schlussrechnung erstellen' : 'Rechnung erstellen'}</button>}
        </>
      }
    >
      {asking && <PrepaymentForm orderId={orderId} onDone={() => setAsking(false)} />}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {list.isLoading && <p className="muted">Wird geladen …</p>}
      {invoices.length > 0 && (
        <ul className="order-list">
          {invoices.map((i) => (
            <li key={i.id}>
              <span className="order-list__main">
                <Link to={`/rechnungen/${i.id}`} className="strong-link">{i.number ?? 'Rechnungsentwurf'}</Link>
                <span className="muted small"> · {SALES_INVOICE_KIND_LABELS[i.kind]}{i.grossCents === null ? '' : ` · ${formatCents(i.grossCents)}`}</span>
              </span>
              <span className={invoiceTone(i.status)}>{SALES_INVOICE_STATUS_LABELS[i.status]}</span>
            </li>
          ))}
        </ul>
      )}
      {canCreate && <p className="muted small">{hasPrepayment ? 'Die Schlussrechnung enthält alle Positionen des Auftrags und zieht die ausgestellten Anzahlungen ab.' : 'Die Rechnung entsteht als Entwurf mit allen Positionen dieses Auftrags. Sie prüfen sie und stellen sie dann aus.'}</p>}
    </CollapsibleCard>
  );
}

/** Anzahlung anfordern: ein Prozentsatz oder ein Bruttobetrag, daraus entsteht der Entwurf einer Anzahlungsrechnung */
function PrepaymentForm({ orderId, onDone }: { orderId: string; onDone: () => void }) {
  const navigate = useNavigate();
  const client = useQueryClient();
  const [mode, setMode] = useState<'percent' | 'amount'>('percent');
  const [value, setValue] = useState('30');
  const [error, setError] = useState<string | null>(null);
  const create = useMutation({
    mutationFn: async () => {
      if (mode === 'percent') {
        const bp = parseBp(value);
        if (bp === null || bp <= 0) throw new ApiError(422, 'validation_failed', 'Bitte einen Prozentsatz größer als 0 angeben.');
        return (await api<{ data: SalesInvoice }>('POST', '/invoices-out/prepayment', { orderId, percentBp: bp })).data;
      }
      const cents = parseCents(value);
      if (cents === null || cents <= 0) throw new ApiError(422, 'validation_failed', 'Bitte einen Betrag größer als 0 angeben.');
      return (await api<{ data: SalesInvoice }>('POST', '/invoices-out/prepayment', { orderId, amountCents: cents })).data;
    },
    onSuccess: (i) => {
      client.invalidateQueries({ queryKey: ['invoices-out'] });
      onDone();
      navigate(`/rechnungen/${i.id}`);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Die Anzahlung konnte nicht angelegt werden.'),
  });
  return (
    <form
      className="payment-form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        create.mutate();
      }}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Anzahlung als</span>
          <select className="input" value={mode} onChange={(e) => { setMode(e.target.value as 'percent' | 'amount'); setValue(e.target.value === 'percent' ? '30' : centsText(0)); }}>
            <option value="percent">Prozent des Auftragswerts</option>
            <option value="amount">Bruttobetrag</option>
          </select>
        </label>
        <label className="field">
          <span>{mode === 'percent' ? 'Prozent' : 'Betrag € (brutto)'}</span>
          <input className="input num" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
        </label>
      </div>
      <p className="muted small">Wirkia verteilt die Anzahlung auf die Steuersätze des Auftrags und legt einen Entwurf an, den Sie prüfen und ausstellen. Die spätere Schlussrechnung zieht sie ab.</p>
      <div className="form-actions">
        <button className="btn btn--primary btn--sm" type="submit" disabled={create.isPending}>{create.isPending ? 'Wird angelegt …' : 'Entwurf anlegen'}</button>
        <button className="btn btn--secondary btn--sm" type="button" onClick={onDone}>Abbrechen</button>
      </div>
    </form>
  );
}
